/**
 * @author <ziad.khoury.hanna@oceandatalab.com>
 * @flow
 */
/*:: import type {ContextRouter} from 'react-router-dom'; */
import '../js/polyfills.js';
import 'preact/debug';
import {html} from 'htm/preact';
import {useState, useEffect} from 'preact/hooks';
import {render, Component} from 'preact';
import PropTypes from 'prop-types';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Link,
  Redirect,
  withRouter,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import {Waypoint} from 'react-waypoint';
/*:: import type {
  APIErrors,
  APIError,
  User,
  Seashot,
  SeashotVisibility,
} from '../js/api.js'; */
import {
  hub as apiHub,
  authUser,
  handlePageData,
  login,
  logout,
  getAuthUser,
  getUserWithId,
  getSeashotWithId,
  updateAuthUser,
  sendVerificationEmail,
  resetPassword,
  updateSeashotWithId,
  likeSeashotWithId,
  deleteSeashotWithId,
} from '../js/api.js';
import {parseQueryString, stringifyQueryString} from '../js/utils.js';
import {
  Ex as ExIcon,
  Search as SearchIcon,
  User as UserIcon,
  Power as PowerIcon,
  QuestionMark as AboutIcon,
} from '../js/components/Icons.js';
import {ESA as ESALogo} from '../js/components/Logos.js';
import {Async} from '../js/components/Async.js';
import {ErrorView} from '../js/components/ErrorView.js';
import {DocumentTitle} from '../js/components/DocumentTitle.js';
import {SeashotViewAsync as SeashotView} from '../js/components/SeashotView.js';
import {SeashotsList} from '../js/components/SeashotsList.js';
import {Store} from '../js/Store.js';
import {SeashotsListFetcher} from '../js/SeashotsListFetcher.js';

/*:: type ViewMode = 'large' | 'medium' | 'small'; */

class SearchField extends Component /*:: <{
  +tag?: string,
  +value?: string,
  +onSearch: (value: string, tag: string | undefined, event: Event) => void,
}> */ {
  static propTypes = {
    tag: PropTypes.string,
    value: PropTypes.string,
    onSearch: PropTypes.func.isRequired,
  };

  input /*: ?HTMLInputElement */ = null;

  refInput = (input /*: ?HTMLInputElement */) => {
    this.input = input;
  };
  handleSearch = (event /*: Event */) => {
    event.preventDefault();
    this.props.onSearch?.(
      this.input?.value ?? this.props.value,
      this.props.tag,
      event
    );
  };
  handleRemoveTag = (event /*: Event */) => {
    event.preventDefault();
    this.props.onSearch?.(
      this.input?.value ?? this.props.value,
      undefined,
      event
    );
  };

  render() {
    //eslint-disable-next-line no-unused-vars
    const {tag, onSearch, ...props} = this.props;

    return html`
      <search className="search-field">
        <form role="search" onSubmit=${this.handleSearch}>
          ${tag
          && html`<span class="tag" title="${tag}">
            <span>${tag}</span>
            <button
              type="button"
              title="Remove tag filter"
              onClick=${this.handleRemoveTag}
            >
              <${ExIcon} role="presentation" />
            </button>
          </span>`}
          <input
            ref=${this.refInput}
            type="search"
            onSearch=${this.handleSearch}
            ...${props}
          />
          <button
            type="submit"
            className="search-button"
            title="Search"
            onClick=${this.handleSearch}
          >
            <${SearchIcon} role="presentation" />
          </button>
        </form>
      </search>
    `;
  }
}

class SeashotsListWithFetcher extends Component /*:: <
  {
    +searchQuery: string,
    +tag?: string,
    +store: Store,
    +seashotsPerPage: number,
    +viewMode: ViewMode,
    +onLikedChange: (seashotId: string, liked: boolean) => void,
  },
  {loading: boolean, canLoadMore: boolean},
> */ {
  /*:: fetcher: SeashotsListFetcher; */

  static propTypes = {
    searchQuery: PropTypes.string.isRequired,
    tag: PropTypes.string,
    store: PropTypes.instanceOf(Store).isRequired,
    seashotsPerPage: PropTypes.number.isRequired,
    viewMode: PropTypes.oneOf(['large', 'medium', 'small']).isRequired,
    onLikedChange: PropTypes.func.isRequired,
  };

  constructor(props /*: Object */) {
    super(props);

    this.state = {
      loading: true,
      canLoadMore: false,
    };
  }

  componentWillMount() {
    this.updateFetcher();
  }
  componentWillReceiveProps(nextProps /*: Object */) {
    if (
      this.props.store !== nextProps.store
      || this.props.searchQuery !== nextProps.searchQuery
      || this.props.tag !== nextProps.tag
    ) {
      this.updateFetcher(nextProps);
    }
  }

  updateFetcher(
    {store, searchQuery, tag, seashotsPerPage} /*: Object */ = this.props
  ) {
    this.fetcher = new SeashotsListFetcher(
      store,
      searchQuery,
      tag,
      0,
      seashotsPerPage
    );
    this.fetcher.hub.on('end', () =>
      this.setState({
        loading: false,
        canLoadMore: this.fetcher.limit < this.fetcher.ids.length,
      })
    );
    this.setState({
      loading: true,
      canLoadMore: false,
    });
    this.fetcher.loadIds();
  }
  loadMoreSeashots() {
    if (this.state.canLoadMore) {
      this.fetcher.limit += this.props.seashotsPerPage;
      this.setState({
        canLoadMore: this.fetcher.limit < this.fetcher.ids.length,
      });
      this.fetcher.loadSeashots();
    }
  }

  handleLoadMore = () => this.loadMoreSeashots();

  render() {
    const {searchQuery, tag, store, viewMode, onLikedChange} = this.props;
    const subTitle =
      (tag ? `[${tag}] ` : '') + (searchQuery ? `${searchQuery} ` : '');

    return html`
      <main className="main list">
        ${subTitle
        && html`<${DocumentTitle} title=${`${subTitle}- SEAShot`} />`}
        <${SeashotsList}
          className="seashots-list"
          viewMode=${viewMode}
          store=${store}
          onLikedChange=${onLikedChange}
        />
        ${this.state.loading
        && html`<div style=${{textAlign: 'center'}}>Loading...</div>`}
        ${this.state.canLoadMore
        && html`<${Waypoint} onEnter=${this.handleLoadMore} />`}
        ${this.state.canLoadMore
        && html`
          <button
            type="button"
            className="load-more"
            onClick=${this.handleLoadMore}
          >
            Load More
          </button>
        `}
      </main>
    `;
  }
}
function renderDocumentTitleForSeashot(seashot /*: Seashot */) {
  return html`<${DocumentTitle} title=${`${seashot.title} - SEAShot`} />`;
}
function renderDocumentTitleForError(error /*: Error */) {
  return html`<${DocumentTitle} title="Error - SEAShot" />`;
}

function goHome(location) {
  return location.pathname === '/'
    ? {...location, search: ''}
    : {...location, pathname: '/'};
}

function HeaderSection({
  onSearch,
} /*: {
  +onSearch: (value: string, tag: string | undefined, event: Event) => void,
} */) {
  const {tag} = useParams();
  const location = useLocation();
  const {q: searchQuery = ''} = parseQueryString(location.search);

  return html`<header className="header">
      <a class="esa-logo" href="https://esa.int/"><${ESALogo} /></a>
      <${Link} className="home-button" to=${goHome}>
        <div class="logo">SEAShot</div>
      </${Link}>
      <${SearchField}
        placeholder="Search..."
        tag=${tag}
        value=${searchQuery.trim()}
        onSearch=${onSearch}
      />
      ${
        authUser
          ? html`
              <${Link}
                className="header-button user-button"
                to="/user"
                title="My profile"
              >
                <${UserIcon} />
              </${Link}>
              <button
                type="button"
                className="header-button logout-button"
                title="Logout"
                onClick=${logout}
              >
                <${PowerIcon} />
              </button>
            `
          : html`
              <button
                type="button"
                className="header-button login-button"
                title="Login"
                onClick=${login}
              >
                <${UserIcon} />
              </button>
            `
      }
      <${Link}
        className="header-button about-button"
        to="/about"
        title="About SEAShot"
      >
        <${AboutIcon} />
      </${Link}>
    </header>`;
}
function AboutSection() {
  return html`
    <main className="main about-view">
      <h1>About</h1>

      <h2>What is SEAShot?</h2>
      <p>
        <em>SEAShot</em> is an online platform to share${' '}
        <a href="https://oceandatalab.com/syntool">Syntool</a> hotspots,
        augmented with a (possibly animated) previsualisation, a description and
        the ability to include annotations. It makes it easy to browse, evaluate
        and share interesting cases with the community.
      </p>

      <p><em>SEAShot</em> is still in beta and is under heavy development.</p>

      <h2>SEAShot vs seashot</h2>
      <p><em>SEAShot</em> is the platform (think <em>Twitter</em>)</p>
      <p>A <em>seashot</em> is a single post (think a <em>tweet</em>)</p>

      <h2>Who made SEAShot?</h2>
      <p>
        <em>SEAShot</em> is developed and operated with ❤️ by${' '}
        <a href="https://oceandatalab.com/">OceanDataLab</a>.
      </p>

      <h2>How is it free?</h2>
      <p>
        <em>SEAShot</em>'s development is sponsored by${' '}
        <a href="https://esa.int/">ESA</a> in context of the${' '}
        <a href="https://www.oceandatalab.com/ovl"
          >Ocean Virtual Lab (OVL-NG)</a
        >
        ${' '} project.
      </p>

      <h2>Where can I <s>complain</s> give my feedback?</h2>
      <p>
        We would love to hear your feedback. So we have put in place a${' '}
        <a href="https://forum.oceandatalab.com/forum-14.html">forum</a>${' '}
        just for that purpose.
      </p>

      <h2>
        Is there a newsletter to keep me up-to-date with OVL tools and
        activities?
      </h2>
      <p>
        Absolutely, the${' '}
        <a href="https://news.oceandatalab.com/subscription/form">
          OVL quarterly newsletter
        </a>
        ${' '}keeps you informed about the latest updates and improvements to
        the OVL tools (OVL Portal, SEAScope, and SEAShot). Each issue highlights
        new products on the OVL portal, enhanced functionalities to streamline
        your workflow, and training materials like tutorials and guides to help
        you maximize the tools efficiency for your needs.
      </p>
    </main>
  `;
}
function AuthUserView({
  user,
  onLabelChange,
} /*: {
  +user: ?User,
  +onLabelChange: (label: string) => void,
} */) {
  if (!authUser) {
    return null;
  }

  const [editing, setEditing] = useState(false);
  const userLabel = editing
    ? html`
        <input value=${user?.label ?? authUser.label} />
        <button
          type="button"
          onClick=${(event) => {
            onLabelChange(event.target.previousElementSibling.value);
            setEditing(false);
          }}
          >Save</button
        >
        <button type="button" onClick=${() => setEditing(false)}>Cancel</button>
      `
    : html`
        ${user?.label ?? authUser.label}
        <button type="button" onClick=${() => setEditing(true)}>Edit</button>
      `;
  return html`
    <h1>Hello ${userLabel}</h1>
    <h2
      >Email: ${authUser.email} (
      ${authUser.email_verified
        ? 'verified'
        : html`<button type="button" onClick=${() => sendVerificationEmail()}
            >send verification email</button
          >`})</h2
    >
    <h2>ID: ${user?.id ?? 'Loading...'}</h2>
    <h2>Joined at: ${user?.joinedAt.toISOString() ?? 'Loading...'}</h2>
    <button type="button" onClick=${() => resetPassword()}
      >Reset password</button
    >
  `;
}
function AuthUserSection({
  initial,
  onLabelChange,
} /*: {
  +initial: APIErrors | User | null,
  +onLabelChange: (label: string) => void,
} */) {
  if (!authUser) {
    return html`<${Redirect} to=${goHome} />`;
  }
  return html`
    <main className="main user-view">
      <${Async}
        promise=${getAuthUser(true /*force*/)}
        initial=${null}
        then=${(user /*: ?User */) =>
          html`<${AuthUserView} user=${user} onLabelChange=${onLabelChange} />`}
        catch=${(error) => html`<${ErrorView} error=${error} />`}
      />
    </main>
  `;
}
function UserViewSection({initial} /*: {+initial: APIErrors | User | null} */) {
  const {userId} = useParams();

  if (!userId) {
    return html`<${Redirect} to=${goHome} />`;
  }
  return html`
    <main className="main user-view">
      <${Async}
        promise=${getUserWithId(userId, true)}
        initial=${initial}
        then=${(user /*: User */) => html`
          <h1>User ${user.label}</h1>
          <h2>Joined at: ${user.joinedAt.toISOString()}</h2>
        `}
        catch=${(error) => html`<${ErrorView} error=${error} />`}
      >
        <p>Loading...</p>
      </${Async}>
    </main>
  `;
}
function SeashotViewSection({
  initial,
  onLikedChange,
  onVisibilityChange,
  onDeleteClick,
} /*: {
  +initial: APIErrors | Seashot | null,
  +onLikedChange: (seashotId: string, liked: boolean) => void,
  +onVisibilityChange: (
    seashotId: string,
    visibility: SeashotVisibility
  ) => void,
  +onDeleteClick: (seashotId: string) => void,
} */) {
  const {seashotId} = useParams();

  if (!seashotId) {
    return html`<${Redirect} to=${goHome} />`;
  }

  const seashotPromise = getSeashotWithId(seashotId, true);
  return html`
    <main className="main view">
      <${Async}
        promise=${seashotPromise}
        initial=${initial}
        then=${renderDocumentTitleForSeashot}
        catch=${renderDocumentTitleForError}
      />
      <${SeashotView}
        seashot=${seashotPromise}
        initial=${initial}
        component="section"
        layout="full"
        onLikedChange=${(liked) => onLikedChange?.(seashotId, liked)}
        onVisibilityChange=${(visibility) =>
          onVisibilityChange?.(seashotId, visibility)}
        onDeleteClick=${() => onDeleteClick?.(seashotId)}
      />
    </main>
  `;
}
function SeashotsListSection({
  store,
  seashotsPerPage,
  initial,
  onLikedChange,
} /*: {
  +store: Store,
  +seashotsPerPage: number,
  +initial: APIErrors | Seashot[] | null,
  +onLikedChange: (seashotId: string, liked: boolean) => void,
} */) {
  const {tag} = useParams();
  const {pathname, search} = useLocation();
  const {q: searchQuery = ''} = parseQueryString(search);

  if (!tag && pathname !== '/') {
    return html`<${Redirect} to=${goHome} />`;
  }

  return html`
    <${SeashotsListWithFetcher}
      searchQuery=${searchQuery.trim()}
      tag=${tag}
      store=${store}
      seashotsPerPage=${seashotsPerPage}
      viewMode="large"
      onLikedChange=${onLikedChange}
    />
  `;
}

class Page extends Component /*:: <
  ContextRouter & {
    +store: Store,
    +seashotsPerPage: number,
    +initial: APIErrors | User | User[] | Seashot | Seashot[] | null,
  },
> */ {
  static propTypes = {
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,

    store: PropTypes.instanceOf(Store).isRequired,
    seashotsPerPage: PropTypes.number.isRequired,
    initial: PropTypes.any,
  };

  async setUserLabel(label /*: string */) {
    await updateAuthUser({label});
    this.forceUpdate();
  }
  async likeSeashot(seashotId /*: string */, liked /*: boolean */) {
    await likeSeashotWithId(seashotId, liked);
    this.forceUpdate();
  }
  async setSeashotVisibility(seashotId /*: string */, visibility /*: SeashotVisibility */) {
    await updateSeashotWithId(seashotId, {visibility});
    this.forceUpdate();
  }
  async deleteSeashot(seashotId /*: string */) {
    await deleteSeashotWithId(seashotId);
    this.props.history.push(goHome(this.props.location));
  }

  handleAuthUserChange = () => {
    this.forceUpdate();
  };
  handleSearch = (value, tag, event) => {
    this.props.history.push({
      ...this.props.location,
      pathname: tag ? `/t/${tag}` : '/',
      search: stringifyQueryString({
        ...parseQueryString(this.props.location.search),
        q: value.trim() || undefined,
      }),
    });
  };
  handleUserLabelChange = (label /*: string */) /*: void */ => {
    this.setUserLabel(label);
  };
  handleLikedChange = (seashotId /*: string */, liked /*: boolean */) /*: void */ => {
    this.likeSeashot(seashotId, liked);
  };
  handleVisibilityChange = async (
    seashotId /*: string */,
    visibility /*: SeashotVisibility */
  ) /*: Promise<void> */ => {
    const seashot = await getSeashotWithId(seashotId);
    const what =
      seashot.visibility === 'unpublished'
        ? 'publish'
        : seashot.visibility === 'private' && visibility === 'unlisted'
          ? `make ${visibility}`
          : null;
    if (what) {
      const yes = window.confirm(
        'This operation cannot be undone!\n'
          + `Are you sure want to ${what} this seashot?`
      );
      if (!yes) {
        return;
      }
    }
    this.setSeashotVisibility(seashotId, visibility);
  };
  handleDeleteClick = (seashotId /*: string */) /*: void */ => {
    const yes = window.confirm(
      'This operation cannot be undone!\n'
        + 'Are you sure want to delete this seashot?'
    );
    if (yes) {
      this.deleteSeashot(seashotId);
    }
  };

  render() {
    const history = useHistory();

    useEffect(() => {
      apiHub.on('change:authUser', this.handleAuthUserChange);
      return () => {
        apiHub.off('change:authUser', this.handleAuthUserChange);
      };
    }, []);
    useEffect(() => {
      if (history.action === 'PUSH') {
        scrollTo(0, 0);
      }
    });

    return html`
      <${DocumentTitle} title="SEAShot" />
      <${Route} path=${['/t/:tag', '/']}>
        <${HeaderSection} onSearch=${this.handleSearch} />
      </${Route}>
      <${Switch}>
        <${Route} path="/u/:userId">
          <${UserViewSection} initial=${this.props.initial} />
        </${Route}>
        <${Route} path="/s/:seashotId">
          <${SeashotViewSection}
            initial=${this.props.initial}
            onLikedChange=${this.handleLikedChange}
            onVisibilityChange=${this.handleVisibilityChange}
            onDeleteClick=${this.handleDeleteClick}
          />
        </${Route}>
        <${Route} path="/user">
          <${AuthUserSection}
            initial=${this.props.initial}
            onLabelChange=${this.handleUserLabelChange}
          />
        </${Route}>
        <${Route} path="/about">
          <${AboutSection} />
        </${Route}>
        <${Route} path=${['/t/:tag', '/']}>
          <${SeashotsListSection}
            store=${this.props.store}
            seashotsPerPage=${this.props.seashotsPerPage}
            initial=${this.props.initial}
            onLikedChange=${this.handleLikedChange}
          />
        </${Route}>
      </${Switch}>
    `;
  }
}

function getIncludedPageData() {
  if (window.__data) {
    try {
      return handlePageData(window.__data);
    } catch (error) {
      return error;
    }
  }
  return null;
}

const store /*: Store */ = new Store();
const PageWithRouter = withRouter(Page);

const rootNode = document.querySelector('.root');

if (!rootNode) {
  throw new Error();
}

render(
  html`<${Router}>
    <${PageWithRouter}
      store=${store}
      seashotsPerPage=${10}
      initial=${getIncludedPageData()}
    />
  </${Router}>`,
  rootNode
);
